/**
 * ReasoningHop + ProvenanceItem — Jan 2026 SOTA "Claude-style live agent
 * activity" types. Streamed in real time via the `reasoning_hop` WS event;
 * accumulated in the FE per-queryId until the answer lands.
 *
 * v1 ref: `ando-frontend/app/types/chat.ts:71-226`.
 */

import { z } from "zod";

export const ReasoningHopActionSchema = z.enum([
  "initial_search",
  "expand_context",
  "refine_query",
  "decompose",
  "multi_query",
  "cross_reference",
]);

export type ReasoningHopAction = z.infer<typeof ReasoningHopActionSchema>;

export const ReasoningHopSpecialistSchema = z.enum([
  "documents",
  "email",
  "chat",
  "web",
  "all",
]);

export type ReasoningHopSpecialist = z.infer<typeof ReasoningHopSpecialistSchema>;

export const ReasoningHopSchema = z.object({
  hop_number: z.number(),
  /** Free-form string at the wire boundary — known values in `ReasoningHopActionSchema`. */
  action: z.string(),
  entities: z.array(z.string()).optional(),
  docs_found: z.number().default(0),
  confidence_delta: z.number().default(0),
  details: z.string().optional(),
  user_details: z.string().optional(),
  top_docs: z.array(z.string()).optional(),
  specialist: z.string().optional(),
  thinking_content: z.string().optional(),
});

export type ReasoningHop = z.infer<typeof ReasoningHopSchema>;

/**
 * Provenance type categorization (Dec 2025 SOTA).
 *   - document: indexed RAG result
 *   - reasoning: agent's own inference step
 *   - web: web_search specialist
 *   - hybrid: blends multiple
 */
export const ProvenanceTypeSchema = z.enum([
  "document",
  "reasoning",
  "web",
  "hybrid",
  "unknown",
]);

export type ProvenanceType = z.infer<typeof ProvenanceTypeSchema>;

export const ProvenanceItemSchema = z.object({
  id: z.string(),
  type: ProvenanceTypeSchema,
  confidence: z.number(),
  // Document
  source_file: z.string().optional(),
  page: z.number().optional(),
  chunk_id: z.string().optional(),
  collection: z.string().optional(),
  // Web
  url: z.string().optional(),
  domain: z.string().optional(),
  fetched_at: z.string().optional(),
  snippet: z.string().optional(),
  // Reasoning
  reasoning_step: z.number().optional(),
  based_on: z.array(z.string()).optional(),
  reasoning_text: z.string().optional(),
  // Display
  title: z.string().optional(),
  preview_text: z.string().optional(),
});

export type ProvenanceItem = z.infer<typeof ProvenanceItemSchema>;

/**
 * Byzantine version-conflict (Jul 2026) — a dirty-data reconciliation the TRAMA
 * consensus pass surfaced: N versions of the same file disagreed on a value and
 * Ando picked one (recency + authority + corroboration weighted PBFT vote). It
 * rides ``provenance_map.byzantine_conflicts`` on the answer frame; the shape
 * mirrors the BE ``format_conflicts_for_prompt`` dict. Rendered by
 * ``<ConflictNote>`` as the answer's "trust receipt".
 */
export const ByzantineConflictVoteSchema = z.object({
  weight: z.number().optional(),
  ratio: z.number().optional(),
  sources: z.array(z.string()).optional(),
  num_claims: z.number().optional(),
});

export const ByzantineConflictSchema = z.object({
  /** The thing in dispute — typically the filename stem (e.g. "contratto acme"). */
  subject: z.string().default(""),
  predicate: z.string().optional(),
  /** The value Ando kept. `string | number` at the wire boundary. */
  consensus_value: z.union([z.string(), z.number()]).nullable().optional(),
  /** "accepted" (quorum) | "conflicted" (no quorum — surfaced but unresolved). */
  consensus_result: z.string().optional(),
  /** Explicit false must never render a leading candidate as accepted. */
  quorum_reached: z.boolean().optional(),
  /** 0–1 agreement confidence. */
  confidence: z.number().optional(),
  /** value → its vote breakdown (weight/ratio/sources). */
  votes: z.record(z.string(), ByzantineConflictVoteSchema).optional(),
  /** Human-readable "why this one" (recency / authority / corroboration). */
  explanation: z.string().optional(),
});

export type ByzantineConflict = z.infer<typeof ByzantineConflictSchema>;
export type ByzantineConflictVote = z.infer<typeof ByzantineConflictVoteSchema>;

/**
 * Extract the byzantine conflicts from a (loosely-typed) ``provenance_map``.
 * Warn-and-drop per item — a malformed conflict never blanks the answer.
 * Returns ``[]`` when the map is absent / not an object / has no conflicts.
 */
export function parseByzantineConflicts(provenanceMap: unknown): ByzantineConflict[] {
  if (typeof provenanceMap !== "object" || provenanceMap === null) return [];
  const raw = (provenanceMap as { byzantine_conflicts?: unknown }).byzantine_conflicts;
  if (!Array.isArray(raw)) return [];
  const out: ByzantineConflict[] = [];
  for (const item of raw) {
    const parsed = ByzantineConflictSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

/**
 * Live reasoning accumulator — what the FE stores per `query_id` while
 * the agent is working, so the UI can render Claude-style live hops.
 */
export interface LiveReasoning {
  hops: ReasoningHop[];
  isActive: boolean;
  /** Final answer has landed — hops freeze, isActive false. */
  finalized?: boolean;

  /**
   * Jul 3, 2026 — SOTA extended-thinking stream. Accumulated verbatim
   * from every ``thinking_delta`` WS event received during the turn.
   * BE-side sanitiser (:func:`_sanitize_thinking_text`) strips tool-call
   * XML, user_facts blocks and credential patterns BEFORE the delta
   * reaches the FE — this field is safe to render as-is.
   *
   * Rendered inside ``<ThinkingChain>`` beneath the hop list (muted
   * italic mono, cursor caret while streaming, collapse-to-chip on
   * ``complete``). Absent when the model didn't emit any thinking (e.g.
   * ``thinking_level=MINIMAL`` on greeting/routing queries).
   */
  streamingThinking?: string;
  /**
   * Timestamp of the FIRST thinking delta received (epoch ms) — drives
   * the "Ragionamento (2.3s · N token)" chip that appears after the
   * answer lands. Zero-cost to leave unset (chip just omits the
   * duration).
   */
  thinkingStartedAt?: number;
  /**
   * Timestamp of the LAST thinking delta received. Frozen on
   * ``finalizeReasoning``. Used with ``thinkingStartedAt`` to compute
   * the chip's elapsed label without a live timer.
   */
  thinkingEndedAt?: number;
}
